import React from 'react';
import { Heart, X } from 'lucide-react';

const CommunityCommentItem = ({
  comment,
  postId,
  onLike,
  onDelete,
  onReply,
  replyingTo,
  newReply,
  setNewReply,
  handleAddReply,
  auth,
  depth = 0,
  isTopLevel = false,
}) => {
  const maxDepth = 5;
  const canReply = depth < maxDepth;
  const isLiked = Boolean(auth.currentUser && comment.likedBy?.includes(auth.currentUser.uid));

  return (
    <div
      className={`rounded-xl ${isTopLevel ? 'p-4' : 'p-3'} ${
        isTopLevel
          ? 'bg-gray-50 dark:bg-gray-800/60'
          : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800'
      }`}
    >
      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-3">
          <img
            src={
              comment.authorAvatar ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.author)}&background=ef4444&color=ffffff&size=${isTopLevel ? '40' : '32'}`
            }
            alt={`${comment.author}'s avatar`}
            className={`${isTopLevel ? 'w-10 h-10' : 'w-8 h-8'} rounded-full object-cover`}
            onError={(e) => {
              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.author)}&background=ef4444&color=ffffff&size=${isTopLevel ? '40' : '32'}`;
            }}
          />
          <div>
            <span className="font-semibold text-sm text-gray-900 dark:text-white">
              {comment.author}
            </span>
            <span className="text-xs ml-2 text-gray-500 dark:text-gray-400">
              {comment.createdAt ? new Date(comment.createdAt).toLocaleDateString() : 'Unknown date'}
            </span>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            type="button"
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition-colors ${
              isLiked
                ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
            }`}
            onClick={() => onLike(comment.id)}
            aria-label={isLiked ? 'Unlike comment' : 'Like comment'}
          >
            <Heart className={`w-3.5 h-3.5 ${isLiked ? 'fill-current' : ''}`} />
            <span>{comment.likes || 0}</span>
          </button>
          {comment.authorId === auth.currentUser?.uid && (
            <button
              type="button"
              onClick={() => onDelete(comment.id)}
              className="p-1 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              aria-label="Delete comment"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <p className="mt-2 text-sm leading-relaxed whitespace-pre-wrap text-gray-700 dark:text-gray-300">{comment.content}</p>

      {canReply && (
        <div className="mt-2 flex items-center space-x-4">
          <button
            type="button"
            onClick={() => onReply(comment.id)}
            className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          >
            Reply
          </button>
        </div>
      )}

      {canReply && replyingTo === comment.id && (
        <div className="mt-3 flex items-start space-x-3">
          <div className="flex-1">
            <textarea
              value={newReply}
              onChange={(e) => setNewReply(e.target.value)}
              placeholder="Add a reply..."
              className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows="2"
            />
          </div>
          <div className="space-x-2">
            <button
              type="button"
              onClick={() => handleAddReply(postId, comment.id)}
              disabled={!newReply.trim()}
              className="px-3 py-1.5 text-sm font-medium bg-red-600 text-white rounded-xl shadow-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Reply
            </button>
            <button
              type="button"
              onClick={() => {
                onReply(null);
                setNewReply('');
              }}
              className="px-3 py-1.5 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {comment.replies?.length > 0 && (
        <div
          className="mt-4 space-y-3 pl-6 border-l-2 border-gray-200 dark:border-gray-700"
        >
          {comment.replies.map((reply) => (
            <CommunityCommentItem
              key={reply.id}
              comment={reply}
              postId={postId}
              onLike={onLike}
              onDelete={onDelete}
              onReply={onReply}
              replyingTo={replyingTo}
              newReply={newReply}
              setNewReply={setNewReply}
              handleAddReply={handleAddReply}
              auth={auth}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommunityCommentItem;
